<template lang="pug">
.online-fields
  BriqInput(v-if="showDealWidgetUrl" label="widget script tag" readonly required :value="widgetUrl")
  BriqInput(
    data-test-id="slug"
    label="Slug"
    v-model="dealSlug" 
    disabled 
    edit-input 
    help="Only lower case letters, numbers and hyphens" 
    :error="slugError"
    @blur="validations.deal.Slug.$touch()"
    )
  BriqInput(
    label="Name"
    name="ExternalName"
    data-test-id="external-name"
    v-model="dealExternalName"
    )
  BriqRichEditorTextarea(label="Description" v-model="deal.Description" data-test-id="deal-online-description")
  BriqTextarea(
    label="USPs"
    help="Put each usp on a new line"
    placeholder="usp 1\nusp 2\nusp 3\nusp 4\nusp 5"
    :rows="5"
    v-model="usps"
    data-test-id="usps"
    )
  ImagePicker(label="Image" name="Image" v-model="deal.ImageLocation" :disabled="disabled")
  .flex.mb-S
    BriqRadioRow.first(
      label="Horizontal Alignment"
      v-model="deal.HorizontalAlignment"
      :options="[{ label: 'Left', value: 'left'}, { label: 'Center', value: 'center'}, { label: 'Right', value: 'right'}]"
      )
    BriqRadioRow(
      label="Vertical Alignment"
      v-model="deal.VerticalAlignment"
      :options="[{ label: 'Top', value: 'top'}, { label: 'Middle', value: 'center'}, { label: 'Bottom', value: 'bottom'}]"
      )
  BriqInput(
    label="Image alt"
    v-model="deal.ImageAltTitle"
    data-test-id="imageAlt"
    )
  BriqInput(label="Shop order" type="number" step="1" v-model="deal.FunnelOrder" data-test-id="deal-online-shop-order")
  BriqCheckbox(label="No index" v-model="deal.SeoNoIndex" data-test-id="deal-online-no-index")
  DealTheme(v-if="businessUnit.Theme" :deal="deal" :businessUnit="businessUnit")
  DealFilters(v-if="businessUnit.Filters" :deal="deal" :businessUnit="businessUnit")
  Program(:deal="deal" :shouldShowMultilanguage="shouldShowMultilanguage" :translationInfo="translationInfo")
</template>

<script>
import { mapGetters } from 'vuex'
import BriqRichEditorTextarea from '@v/components/BriqRichEditorTextarea.vue'
import ImagePicker from '@v/components/ImagePicker.vue'
import BriqRadioRow from './BriqRadioRow.vue'
import DealFilters from './DealFilters.vue'
import DealTheme from './DealTheme.vue'
import Program from './Program.vue'
import toKebab from '@/helpers/toKebab.js'
import slugify from '@/helpers/slugify.js'

export default {
  name: 'OnlineFields',
  components: {
    BriqRichEditorTextarea,
    ImagePicker,
    BriqRadioRow,
    DealFilters,
    DealTheme,
    Program,
  },
  inject: ['validations'],
  props: {
    deal: { type: Object, required: true },
    businessUnit: { type: Object, required: true },
    disabled: Boolean,
    funnelBaseUrl: String,
    shouldShowMultilanguage: Boolean,
    translationInfo: Object,
  },
  data() {
    return {
      shouldAutoSetSlug: !this.deal.Slug,
    }
  },
  computed: {
    ...mapGetters('briqState', ['featureFlagIsEnabled']),
    showDealWidgetUrl() {
      return this.featureFlagIsEnabled('enable-show-deal-widget-url')
    },
    widgetUrl() {
      if (!this.deal.Slug) return 'Please enter a slug'
      if (!this.funnelBaseUrl) return 'Funnel base URL is unknown'
      const buDomain = this.businessUnit.Domain
      const funnelBase = this.funnelBaseUrl
      const slug = this.deal.Slug || '[please enter a slug]'
      const widgetSrc = `${buDomain}.${funnelBase}/briqwidget/${slug}`
      // eslint-disable-next-line no-useless-escape
      return `<script type="text/javascript" src="${widgetSrc}"><\/script>`
    },
    usps: {
      get() {
        return this.deal.Usps?.join('\n')
      },
      set(newVal) {
        this.deal.Usps = newVal.split('\n')
      },
    },
    slugError() {
      return (
        (this.validations.deal.Slug.$dirty &&
          this.validations.deal.Slug.$invalid &&
          'Slug should consist of only lowercase letters, numbers and hyphens') ||
        null
      )
    },
    dealExternalName: {
      get() {
        return this.deal.ExternalName
      },
      set(val) {
        this.deal.ExternalName = val
        this.maybeUpdateSlug(val)
      },
    },
    dealSlug: {
      get() {
        return this.deal.Slug
      },
      set(val) {
        this.deal.Slug = toKebab(val)
      },
    },
  },
  methods: {
    maybeUpdateSlug(val) {
      if (this.shouldAutoSetSlug) {
        if (typeof this.deal.Slug !== 'undefined') this.deal.Slug = slugify(val)
      }
    },
  },
}
</script>

<style lang="stylus">
@import '~@v/variables.styl'

.online-fields
  .briq-form-element
    margin-bottom space-S
</style>

<style scoped lang="stylus">
@import '~@v/variables.styl'

.first
  margin-right space-M
</style>
